import {
  Button,
  fade,
  Grid,
  makeStyles,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Autocomplete, AutocompleteCloseReason } from "@material-ui/lab";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { DeleteAttributeCardComponent } from "../../components/DeleteAttributeCard";
import { appColor } from "../../constants/customThemeColor";
import { useCustomStyle } from "../../../styles";
import {
  ResponseAttribute,
  ShareFeedbackAttributeNameAndId,
  AttributeQuestionEmoji,
  ResponseScaleProp,
  State,
} from "../../../type";
import { AttributeTitleComponent } from "../share_feedback/AttributeTitle";
import {
  RenderAttributeQuestion,
  RenderContext,
  EditorForText,
} from "../share_feedback/addNewButtonElements";
import { updateAttributeOnClick } from "./addNewButtonHandles";
import { QuestionAnswerRenderer } from "./QuestionAnswerRenderer";
import { AddNewButtonProps } from "./type";
import { EditorState } from "draft-js";
import { buildExplanationStates, isExplanationEmpty, editorStateToHtml } from "../explanationHelpers";

const useStyle = makeStyles((theme) => ({
  popper: {
    border: "1px solid rgba(27,31,35,.15)",
    boxShadow: "0 3px 12px rgba(27,31,35,.15)",
    borderRadius: 3,
    width: 300,
    zIndex: 1,
    fontSize: 13,
    color: "#586069",
    backgroundColor: "#f6f8fa",
  },
  textField: {
    padding: 10,
    width: "100%",
    borderBottom: "1px solid #dfe2e5",
    "& input": {
      borderRadius: 4,
      backgroundColor: theme.palette.common.white,
      padding: 8,
      transition: theme.transitions.create(["border-color", "box-shadow"]),
      border: "1px solid #ced4da",
      fontSize: "14px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "10px",
      },
      "&:focus": {
        boxShadow: `${fade(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
        borderColor: theme.palette.primary.main,
      },
    },
  },
  paper: {
    width: "100%",
    padding: "0px 0px 10px 0px",
    marginTop: "20px",
    marginBottom: "20px",
    textAlign: "start",
  },
  btn: {
    margin: theme.spacing(3, 0, 0, 0),
    backgroundColor: "#576BEA",
  },
  typography_body_1: {
    alignItems: "center",
    color: "#FFFFFF",
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  typography_body_3: {
    marginLeft: theme.spacing(0.5),
    alignItems: "center",
    color: "#000000",
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
    },
  },
  skipCTA: {
    fontSize: theme.spacing(1.75),
    fontFamily: "Montserrat",
    fontWeight: 600,
    color: "#576BEA",
    textDecoration: "underline",
    margin: theme.spacing(0.25, 1.5, 0, 0),
    [theme.breakpoints.down("xs")]: {
      fontSize: theme.spacing(1.25),
      margin: theme.spacing(0.25, 0, 0, 0),
    },
  },
}));

export const AddNewButton: React.FC<AddNewButtonProps> = ({
  attributes,
  setAttributes,
  shareAssessmentData,
  setShareAssessmentData,
  setValue,
  onAnyChange
}) => {
  const classes = useStyle();
  const customClasses = useCustomStyle();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const attr: Array<ResponseAttribute> = [];
  const attLabels =
    useSelector((state: State) => state.data.data?.attributes) ?? attr;
  const emptyList: Array<ResponseScaleProp> = [];
  const responseScales =
    useSelector((state: State) => state.data.data?.scaleResponseEntityList) ?? emptyList;
  const [onDeleteAttribute, setOnDeleteAttribute] = React.useState<number>(0);
  const [emojiSelected, setEmojiSelected] = React.useState<Array<AttributeQuestionEmoji>>([]);
  const [questionExplanationStates, setQuestionExplanationStates] = React.useState<
    Array<{ explanations: Array<EditorState> }>
  >([]);

  useEffect(() => {
    if (shareAssessmentData.length > 0 && emojiSelected.length !== shareAssessmentData.length) {
      const emojis: Array<AttributeQuestionEmoji> = shareAssessmentData.map((attr) => ({
        attributeId: attr.attributeId,
        queEmoji: attr.questions.map((q) => {
          if (q.scaleName === "VRIDDHI") {
            const scale = responseScales.find((s) => s.scaleName === "VRIDDHI" && s.scaleVersion === q.scaleVersion);
            const savedAnswers: string[] = (() => { try { return JSON.parse(q.answer); } catch { return []; } })();
            const emojiIds = scale
              ? savedAnswers.map((ans) => scale.scaleResponses.findIndex((r) => r.url === ans)).filter((i) => i !== -1)
              : [];
            return { questionId: q.questionId, emojiId: emojiIds };
          }
          const scale = responseScales.find((s) => s.scaleName === q.scaleName && s.scaleVersion === q.scaleVersion);
          const emojiId = scale ? scale.scaleResponses.findIndex((r) => r.url === q.answer) : -1;
          return { questionId: q.questionId, emojiId };
        }),
      }));
      setEmojiSelected(emojis);
    }
  }, [shareAssessmentData, responseScales]);

  const handleExplanationChange = (state, attrIdx, questionIdx) => {
    const html = editorStateToHtml(state);
    const updatedData = [...shareAssessmentData];
    updatedData[attrIdx] = { ...updatedData[attrIdx], questions: [...updatedData[attrIdx].questions] };
    updatedData[attrIdx].questions[questionIdx] = { ...updatedData[attrIdx].questions[questionIdx], explanation: html };
    setShareAssessmentData(updatedData);

    const newStates = [...questionExplanationStates];
    if (!newStates[attrIdx]) {
      newStates[attrIdx] = { explanations: [] };
    }
    newStates[attrIdx].explanations[questionIdx] = state;
    setQuestionExplanationStates(newStates);

    if (onAnyChange) onAnyChange(true);
  };

  useEffect(() => {
    if (shareAssessmentData.length > 0 && questionExplanationStates.length !== shareAssessmentData.length) {
      setQuestionExplanationStates(buildExplanationStates(shareAssessmentData, questionExplanationStates));
    }
  }, [shareAssessmentData]);

  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [deletingAttibute, setDeletingAttribute] =
    React.useState<ShareFeedbackAttributeNameAndId>({
      attributeId: 0,
      attributeName: "",
    });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDelete = (id: number) => {
    setAttributes(attributes.filter((attribute) => attribute.id !== id));
    handleModalClose();
    setOnDeleteAttribute(1);
    onAnyChange(true);
  };

  useEffect(() => {
    if (onDeleteAttribute === 1) {
      updateAttributeOnClick(
        attributes,
        shareAssessmentData,
        setShareAssessmentData,
        emojiSelected,
        setEmojiSelected
      );
    }
    setOnDeleteAttribute(0);
  }, [onDeleteAttribute]);

  const handleClose = (
    event: React.ChangeEvent<{}>,
    reason: AutocompleteCloseReason
  ) => {
    if (reason === "toggleInput") {
      return;
    }
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleModalOpen = () => {
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
  };

  const renderDeletionModal = () => (
    <DeleteAttributeCardComponent
      openModal={openModal}
      deletingAttribute={deletingAttibute}
      handleDelete={handleDelete}
      handleModalClose={handleModalClose}
    />
  );

  return (
    <>
      <Grid
        container
        alignItems="flex-start"
        direction="column"
        className="assessment"
      >
        {shareAssessmentData.map((attribute, idx) => (
          <Paper
            variant="outlined"
            className={classes.paper}
            key={attribute.attributeId}
            id={attribute.attributeId.toString()}
          >
            <Grid key={attribute.attributeId} xs={12} item container>
              {<AttributeTitleComponent attribute={attribute} setDeletingAttribute={setDeletingAttribute} handleModalOpen={handleModalOpen} />}

              {attribute.questions.map((question, idx_1) => (
                <Grid xs={12} item container key={question.questionId}>
                  <Grid item xs={12} container>
                  <RenderAttributeQuestion
                    question={question.questionName}
                    answer={question.answer}
                  />
                  <QuestionAnswerRenderer
                    scaleName={question.scaleName}
                    question={question}
                    idx={idx}
                    idx_1={idx_1}
                    emojiSelected={emojiSelected}
                    setEmojiSelected={setEmojiSelected}
                    shareFeedbackData={shareAssessmentData}
                    setShareFeedbackData={setShareAssessmentData}
                    onAnyChange={onAnyChange}
                    responseScales={responseScales}
                  />

                  {/* Explanation Field */}
                  <Grid item xs={12} style={{ marginTop: 16, marginBottom: 16, paddingLeft: 20, paddingRight: 20 }}>
                    <Typography gutterBottom variant="h5">
                      {"Explanation/Details "}
                      {isExplanationEmpty(question.explanation) && (
                        <span
                          style={{ color: `${appColor.redWarn}` }}
                          className={customClasses.typography_font_14_8_500}
                        >
                          (required)
                        </span>
                      )}
                    </Typography>
                    <EditorForText
                      idx={idx * 1000 + idx_1}
                      textHeader="explanation"
                      editorState={
                        questionExplanationStates[idx]?.explanations[idx_1] ||
                        EditorState.createEmpty()
                      }
                      onEditorStateChange={(state) => handleExplanationChange(state, idx, idx_1)}
                      placeHolderText="Provide detailed explanation for this question"
                    />
                  </Grid>
                  </Grid>
                </Grid>
              ))}

              {attribute.questions.length === 0 && (
              <RenderContext
                attribute={attribute}
                idx={idx}
                shareFeedbackData={shareAssessmentData}
                setShareFeedbackData={setShareAssessmentData}
                setValue={setValue}
                onAnyChange={onAnyChange}
              />
              )}
            </Grid>
          </Paper>
        ))}
      </Grid>
      <Grid container justify="center" alignItems="center">
        {renderDeletionModal()}
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={handleClick}
        className={classes.btn}
      >
        <Typography variant="h2" className={classes.typography_body_1}>
          ADD NEW
        </Typography>
      </Button>
      <Popper
        id="popper"
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.popper}
      >
        <Autocomplete
          open
          onClose={handleClose}
          filterSelectedOptions
          multiple
          value={attributes}
          onChange={(event, newValue) => {
            setAttributes(newValue);
            updateAttributeOnClick(
              newValue,
              shareAssessmentData,
              setShareAssessmentData,
              emojiSelected,
              setEmojiSelected
            );
          }}
          disablePortal
          getOptionSelected={(option, value) => option.id === value.id}
          renderOption={(option) => (
            <React.Fragment>
              <div className={classes.typography_body_3}>
                <Typography
                  variant="h5"
                  className={classes.typography_body_3}
                >
                  {option.name}
                </Typography>
              </div>
            </React.Fragment>
          )}
          options={attLabels}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              placeholder="Search attribute by name"
              variant="outlined"
              ref={params.InputProps.ref}
              autoFocus
              inputProps={params.inputProps}
              className={classes.textField}
            />
          )}
        />
      </Popper>
    </>
  );
};
